import React, { useEffect, useState } from 'react';
import { ChevronDown, Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useChartActions } from '../../context/ChartActionsContext';

/**
 * Dynamic Clinical Context section for Treatment Plan create/edit dialog.
 * Renders fields from the `treatment_plan_clinical_context` form template.
 *
 * Add-Diagnosis / Add-Medication CTAs are routed through
 * `useChartActions()` (Feb 2026) so the dialog open flow is the
 * same whether this component is rendered inside the legacy
 * Dashboard or a V2 chart — no prop drilling required.  See
 * `/app/frontend/src/context/ChartActionsContext.jsx` for the
 * contract.
 */
export const TreatmentPlanClinicalContext = ({
  formTemplates,
  // Diagnoses
  tpDiagnoses, setTpDiagnoses,
  // Medications
  tpMedications, setTpMedications,
  // Level of care
  tpLevelOfCareNotes, setTpLevelOfCareNotes,
  // Trauma reports
  tpTraumaReports, setTpTraumaReports,
  // Supports
  tpSupportsNoneReported, setTpSupportsNoneReported,
  tpSupports, setTpSupports,
  // Collapse state
  collapsed, setCollapsed,
}) => {
  const chartActions = useChartActions();
  // Wrap in zero-arg lambdas so the Button's MouseEvent doesn't
  // accidentally become the `client` argument of the context
  // setter (which would then set selectedClient to a SyntheticEvent).
  const onAddDiagnosis = () => chartActions.openAddDiagnosis();
  const onAddMedication = () => chartActions.openAddMedication();

  const [fields, setFields] = useState([]);

  // Descriptions for known fields (always shown regardless of template)
  const fieldDescriptions = {
    'loc': 'How will the provider/person served/parent/guardian know that level of care change is warranted and what plans are being discussed at this point in treatment to prepare?',
    'trauma': 'New reports of trauma, abuse, exploitation, or neglect and any actions taken in response to them.',
  };

  // Load form fields from template
  useEffect(() => {
    if (!formTemplates?.length) return;
    const template = formTemplates.find(t => t.form_type === 'treatment_plan_clinical_context');
    if (template?.form_fields) {
      const sorted = [...template.form_fields]
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(f => ({
          ...f,
          description: f.description || fieldDescriptions[f.id] || '',
        }));
      setFields(sorted);
    }
  }, [formTemplates]);

  // Fallback: if no template found, use default field order
  const defaultFields = [
    { id: 'dx', type: 'client_diagnoses', label: 'Diagnoses', sort_order: 1 },
    { id: 'meds', type: 'client_medications', label: 'Medications Review', sort_order: 2 },
    { id: 'loc', type: 'textarea', label: 'Level of Care Change', sort_order: 3, description: 'How will the provider/person served/parent/guardian know that level of care change is warranted and what plans are being discussed at this point in treatment to prepare?', placeholder: 'Enter level of care notes...' },
    { id: 'trauma', type: 'textarea', label: 'New Reports of Trauma, Abuse, Exploitation, or Neglect', sort_order: 4, description: 'New reports of trauma, abuse, exploitation, or neglect and any actions taken in response to them.', placeholder: "Enter any new reports or 'None reported'..." },
    { id: 'supports', type: 'supports_matrix', label: 'Other Agencies / Community Supports and Resources', sort_order: 5, default_roles: ['PCP', 'Psychiatrist/Psych NP', 'Therapist (outpatient)', 'Therapist (In Home/Behavioral)', 'TM', 'TT&S', 'DCF Worker', 'ICC', 'School (include grade)', 'Probation Officer', 'Parent/Guardian', 'Emergency Contact', 'Guardian Ad Litem'] },
  ];

  const activeFields = fields.length > 0 ? fields : defaultFields;

  const renderField = (field) => {
    switch (field.type) {
      case 'client_diagnoses':
        return <DiagnosesField key={field.id} field={field} diagnoses={tpDiagnoses} setDiagnoses={setTpDiagnoses} onAdd={onAddDiagnosis} />;
      case 'client_medications':
        return <MedicationsField key={field.id} field={field} medications={tpMedications} setMedications={setTpMedications} onAdd={onAddMedication} />;
      case 'supports_matrix':
        return <SupportsField key={field.id} field={field} supports={tpSupports} setSupports={setTpSupports} noneReported={tpSupportsNoneReported} setNoneReported={setTpSupportsNoneReported} />;
      case 'textarea':
        return <TextareaField key={field.id} field={field} value={getTextareaValue(field)} onChange={getTextareaOnChange(field)} />;
      default:
        return null;
    }
  };

  // Map textarea fields to their state based on label/id matching
  const getTextareaValue = (field) => {
    const label = (field.label || '').toLowerCase();
    if (label.includes('level of care')) return tpLevelOfCareNotes;
    if (label.includes('trauma') || label.includes('abuse') || label.includes('neglect')) return tpTraumaReports;
    return '';
  };

  const getTextareaOnChange = (field) => {
    const label = (field.label || '').toLowerCase();
    if (label.includes('level of care')) return setTpLevelOfCareNotes;
    if (label.includes('trauma') || label.includes('abuse') || label.includes('neglect')) return setTpTraumaReports;
    return () => {};
  };

  return (
    <div className="border-t mt-4 pt-1" data-testid="clinical-context-section">
      <button
        type="button"
        className="w-full flex items-center gap-2 py-2 px-1 hover:bg-gray-50 transition-colors rounded"
        onClick={() => setCollapsed(prev => !prev)}
        data-testid="clinical-context-toggle"
      >
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        <h3 className="text-sm font-semibold text-gray-800">Clinical Context</h3>
      </button>
      {!collapsed && (
        <div className="px-1 pb-2 space-y-4">
          {activeFields.map(renderField)}
        </div>
      )}
    </div>
  );
};

/* ─── Sub-components ─── */

const DiagnosesField = ({ field, diagnoses, setDiagnoses, onAdd }) => (
  <div className="border rounded-lg p-4" data-testid="tp-diagnoses-section">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-gray-800">{field.label}</h3>
      <Button type="button" variant="outline" size="sm" onClick={onAdd} data-testid="tp-add-diagnosis-btn">
        <Plus className="h-3.5 w-3.5 mr-1" /> Add Diagnosis
      </Button>
    </div>
    {diagnoses.length > 0 ? (
      <div className="space-y-2 mb-3">
        {diagnoses.map((d, idx) => {
          const provNames = d.provider_names?.length > 0 ? d.provider_names.join(', ') : (d.assigned_by_name || '');
          return (
            <div key={idx} className="flex items-center gap-2 text-sm bg-gray-50 p-2 rounded" data-testid={`tp-diagnosis-item-${idx}`}>
              <span className="font-mono text-xs text-blue-600 min-w-[60px]">{d.code}</span>
              <span className="flex-1">{d.description}</span>
              {d.is_primary && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Primary</span>}
              {provNames && <span className="text-xs text-gray-400">{provNames}</span>}
              <button type="button" className="text-red-400 hover:text-red-600" onClick={() => setDiagnoses(prev => prev.filter((_, i) => i !== idx))} data-testid={`tp-remove-diagnosis-${idx}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    ) : (
      <p className="text-sm text-gray-400 italic">No diagnoses on file.</p>
    )}
  </div>
);

const MedicationsField = ({ field, medications, setMedications, onAdd }) => (
  <div className="border rounded-lg p-4" data-testid="tp-medications-section">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-gray-800">{field.label}</h3>
      <Button type="button" variant="outline" size="sm" onClick={onAdd} data-testid="tp-add-medication-btn">
        <Plus className="h-3.5 w-3.5 mr-1" /> Add Medication
      </Button>
    </div>
    {medications.length > 0 ? (
      <div className="space-y-2 mb-3">
        {medications.map((m, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm bg-gray-50 p-2 rounded" data-testid={`tp-medication-item-${idx}`}>
            <span className="font-medium flex-1">{m.name}</span>
            <span className="text-gray-500 text-xs">{[m.dosage, m.frequency].filter(Boolean).join(' | ')}</span>
            {m.prescriber && <span className="text-xs text-gray-400">({m.prescriber})</span>}
            {m.purpose && <span className="text-xs text-purple-600">{m.purpose}</span>}
            <button type="button" className="text-red-400 hover:text-red-600" onClick={() => setMedications(prev => prev.filter((_, i) => i !== idx))} data-testid={`tp-remove-medication-${idx}`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-400 italic">No active medications on file.</p>
    )}
  </div>
);

const TextareaField = ({ field, value, onChange }) => (
  <div className="border rounded-lg p-4" data-testid={`tp-textarea-${field.id}`}>
    <h3 className="text-sm font-semibold text-gray-800 mb-2">{field.label}</h3>
    {field.description && <label className="text-xs text-gray-500 block mb-1">{field.description}</label>}
    <textarea
      className="w-full border rounded p-2 text-sm"
      rows={field.rows || 4}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
      data-testid={`tp-textarea-input-${field.id}`}
    />
  </div>
);

const SupportsField = ({ field, supports, setSupports, noneReported, setNoneReported }) => {
  const roles = field.default_roles || [
    'PCP', 'Psychiatrist/Psych NP', 'Therapist (outpatient)', 'Therapist (In Home/Behavioral)',
    'TM', 'TT&S', 'DCF Worker', 'ICC', 'School (include grade)',
    'Probation Officer', 'Parent/Guardian', 'Emergency Contact', 'Guardian Ad Litem'
  ];

  const addSupport = () => setSupports(prev => [...prev, { role: '', contact_and_title: '', services_provided: '', release_signed: '', isCustomRole: false }]);
  const updateSupport = (idx, updates) => setSupports(prev => prev.map((s, i) => i === idx ? { ...s, ...updates } : s));
  const removeSupport = (idx) => setSupports(prev => prev.filter((_, i) => i !== idx));

  return (
    <div className="border rounded-lg p-4" data-testid="tp-supports-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-800">{field.label}</h3>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={noneReported} onChange={(e) => setNoneReported(e.target.checked)} data-testid="tp-supports-none-reported" />
            None Reported
          </label>
          {!noneReported && (
            <Button type="button" variant="outline" size="sm" onClick={addSupport} data-testid="tp-add-support-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Support
            </Button>
          )}
        </div>
      </div>
      {!noneReported && (
        <>
          {supports.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left p-2 border text-xs font-medium text-gray-600 w-[180px]">Role</th>
                    <th className="text-left p-2 border text-xs font-medium text-gray-600">Contact and Title</th>
                    <th className="text-left p-2 border text-xs font-medium text-gray-600">Services Currently Provided</th>
                    <th className="text-left p-2 border text-xs font-medium text-gray-600 w-[100px]">Release Signed</th>
                    <th className="p-2 border w-[40px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {supports.map((s, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="p-1 border">
                        {s.isCustomRole ? (
                          <input type="text" className="w-full border rounded px-1.5 py-1 text-xs" placeholder="Custom role" value={s.role} onChange={(e) => updateSupport(idx, { role: e.target.value })} />
                        ) : (
                          <select className="w-full border rounded px-1 py-1 text-xs bg-white" value={s.role} onChange={(e) => {
                            if (e.target.value === '__custom__') updateSupport(idx, { role: '', isCustomRole: true });
                            else updateSupport(idx, { role: e.target.value });
                          }}>
                            <option value="">Select role...</option>
                            {roles.map(r => <option key={r} value={r}>{r}</option>)}
                            <option value="__custom__">Other (custom)...</option>
                          </select>
                        )}
                      </td>
                      <td className="p-1 border">
                        <input type="text" className="w-full border rounded px-1.5 py-1 text-xs" placeholder="Enter text" value={s.contact_and_title} onChange={(e) => updateSupport(idx, { contact_and_title: e.target.value })} />
                      </td>
                      <td className="p-1 border">
                        <input type="text" className="w-full border rounded px-1.5 py-1 text-xs" placeholder="Enter text" value={s.services_provided} onChange={(e) => updateSupport(idx, { services_provided: e.target.value })} />
                      </td>
                      <td className="p-1 border">
                        <select className="w-full border rounded px-1 py-1 text-xs" value={s.release_signed} onChange={(e) => updateSupport(idx, { release_signed: e.target.value })}>
                          <option value="">Select</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                          <option value="N/A">N/A</option>
                        </select>
                      </td>
                      <td className="p-1 border text-center">
                        <button type="button" className="text-red-400 hover:text-red-600" onClick={() => removeSupport(idx)} data-testid={`tp-remove-support-${idx}`}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {supports.length === 0 && <p className="text-sm text-gray-400 italic mb-2">No supports added yet.</p>}
        </>
      )}
    </div>
  );
};

export default TreatmentPlanClinicalContext;
